import * as THREE from 'three'
import { types, Instance, getParent, getSnapshot } from 'mobx-state-tree'
import { Element } from '../Element'
import { generateNodeType } from 'src/api/models/utils/generateNodeType'
import { ReferencePoint } from 'src/constants/referencePoints'
import { elementEdge } from 'src/constants/typenames'
import { FocusOptions } from 'src/api/models/utils/generateNodeType'
import { Translation } from 'src/utils/getTranslation'
import { getReferencePoints2D } from 'src/utils/getReferencePoints'
import { ElementEdgeProperties } from './ElementEdgeProperties'
import { ICoverPlateEdge } from 'src/api/models/Enclosure/Cover/types'
import { IElementEdge } from './types'
import { IElementConnection } from '../types'
import { ElementEdgeReference } from './ElementEdgeReference'
import { IJsonPatchInput } from 'src/api/misc/Drafter/types'

export const ElementEdge = (): IElementEdge =>
  generateNodeType(elementEdge)
    .props({
      node: Element,
      properties: types.optional(ElementEdgeProperties, {}),
      reference: types.optional(ElementEdgeReference, {})
    })
    .views((self) => {
      return {
        /**
         * returns true, false or null if no element in the root is selected
         */
        get isSelected() {
          if (!self.routeState.view?.currentView.selectedItem) return null
          return (
            self.routeState.view?.currentView.selectedItem?.id === self.id ||
            false
          )
        },
        get side() {
          // 4. CoverPlateEdge 3. CoverPlate 2. ElementConnection 1. types.array
          const coverPlateEdge = getParent<Instance<ICoverPlateEdge>>(self, 4)
          return coverPlateEdge.properties.side
        },
        get referencePoints() {
          return self.node.getReferencePoints()
        }
      }
    })
    .views((self) => ({
      get computedOffset() {
        const coverPlateEdge = getParent<Instance<ICoverPlateEdge>>(self, 4)
        const { matrix, position = [0, 0, 0] } = coverPlateEdge.properties
          .translation as Translation
        const point = new THREE.Vector3(...position)
        if (matrix) point.applyMatrix4(new THREE.Matrix4().set(...matrix))

        const b2 = new THREE.Box2().setFromCenterAndSize(
          new THREE.Vector2(),
          new THREE.Vector2(
            self.node.properties.width.value as number,
            self.node.properties.height.value as number
          )
        )

        const elementOffset = getReferencePoints2D(b2)[
          self.properties.elementReferencePoint.value as ReferencePoint
        ]

        const x = -elementOffset[0] + self.properties.offset.x.value
        const y = -elementOffset[1] + self.properties.offset.y.value

        const [px, py] = self.properties.parentReferencePointCoordinates
        return { x: px + x, y: py + y }
      }
    }))
    .actions((self) => ({
      /**
       *
       * @param param0 [ IJsonPatch[] | IJsonPatch["value"], reverseChange ]
       * @param patch
       * @param path
       */
      patch(
        [change, reverseChange]: [any, any],
        patch: boolean | undefined = undefined,
        path?: string
      ) {
        if (path) {
          return [change, reverseChange].map((inputChange) => {
            if (Array.isArray(inputChange)) {
              return inputChange.map((input) =>
                input.path && input.value
                  ? {
                      op: input.op ?? 'replace',
                      path: `${path}${input.path}`,
                      value: input.value
                    }
                  : { op: 'replace', path, value: input }
              )
            } else {
              return inputChange.path && inputChange.value
                ? {
                    op: inputChange.op ?? 'replace',
                    path: `${path}${inputChange.path}`,
                    value: inputChange.value
                  }
                : { op: 'replace', path, value: inputChange }
            }
          }) as [IJsonPatchInput, IJsonPatchInput]
        }
      },
      select() {
        self.routeState.state?.name === 'editor' &&
          self.routeState.view.selectItem(self)
      },
      unselect() {
        self.routeState.state?.name === 'editor' &&
          self.routeState.view.currentView.selectedItem === self &&
          self.routeState.view.unselectItem()
      },
      focusReferencePoint(options: FocusOptions) {
        self.focusedReferencePoint = options.referencePoint
        if (options.side) self.focusedSide = options.side
        self.node.focusReferencePoint(options)
      },
      focus(options?: Partial<FocusOptions>) {
        const referencePoint =
          options?.referencePoint || self.properties.elementReferencePoint.value
        self.focusedReferencePoint = referencePoint
        const side = options?.side || self.side
        self.focusedSide = side
        self.focused = true
        self.node.focus({ referencePoint, side })
      },
      blur() {
        self.focused = false
        self.focusedSide = null
        self.focusedReferencePoint = null
        self.node.blur()
      },
      delete() {
        // 2. ElementConnection 1. types.array
        const parent = getParent<Instance<IElementConnection>>(self, 2)
        parent.removeElement(self.id, true)
      },
      clone() {
        const parent = getParent<Instance<IElementConnection>>(self, 2)
        const {
          id: edgeID,
          node: { id, ...node },
          ...clonedElement
        } = getSnapshot(self)
        console.log(node, clonedElement)
        parent.addElement({ node, ...clonedElement })
      },
      connect(nextParent: Instance<IElementConnection>, index?: number) {
        const parent = getParent<Instance<IElementConnection>>(self, 2)
        parent.detachElement(self.id, false)
        nextParent.addElement(self as $FixMe, false)
      }
    }))
